/**
 * صفحه دانلود اپلیکیشن اندروید
 * ==============================
 * دو راه دانلود عمداً کنار هم است:
 *
 * • **کافه‌بازار** — برای کاربری که می‌خواهد به‌روزرسانی خودکار بگیرد و به
 *   نصب از فروشگاه عادت دارد.
 * • **فایل مستقیم** — برای کاربری که بازار ندارد. در ایران این حالت کم نیست.
 *
 * و یک چیز که معمولاً فراموش می‌شود: کاربری که APK را مستقیم دانلود می‌کند،
 * اندروید سر راهش هشدار «منبع ناشناس» می‌گذارد و بیشترشان همان‌جا منصرف
 * می‌شوند. برای همین مراحل نصب همین‌جا و پیش از دانلود توضیح داده شده.
 */
import { useEffect, useState } from 'react'
import {
  Smartphone, Download, ShieldCheck, Lock, LifeBuoy, Zap, Check, Info, Share2, Plus, Monitor,
} from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, Button, Rows, Empty, CopyButton } from '../components/UI'
import { get } from '../lib/api'
import { useSeo } from '../lib/seo'
import { fa, num } from '../lib/format'
import { isNativeApp } from '../lib/native'

/** نشانی مطلق — دکمه‌ی «کپی لینک» باید چیزی بدهد که در سافاری قابل چسباندن باشد. */
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : ''

type AppInfo = {
  enabled: boolean; versionCode: number; versionName: string
  url: string; bazaarUrl: string; notes: string; sizeMb: number; minAndroid: string
  windows?: { enabled: boolean; url: string; version: string; sizeMb: number }
}

/*
 * ── چرا دو قابلیت از این فهرست حذف شد ──────────────────────────────────
 * اینجا «قفل با اثر انگشت» و «اعلان قیمت» نوشته شده بود. هر دو از دورانِ
 * معماری قبلی مانده بودند که اپ یک پوسته‌ی WebView بود و پلی به نام
 * `ArzinoApp` روی `window` می‌گذاشت.
 *
 * اپ امروز بومی و با Compose نوشته شده و هیچ‌کدام را ندارد — کتابخانه‌ی
 * اثر انگشت اصلاً از پروژه حذف شد و APK نهایی تنها دو مجوز دارد. وعده‌ی
 * قابلیتی که در اپ نیست، هم کاربر را ناامید می‌کند و هم در بازبینی
 * فروشگاه سؤال می‌سازد؛ جایشان دو چیز نشسته که واقعاً هست و از روی همان
 * فهرستِ دو مجوزی قابل راستی‌آزمایی است.
 */
const FEATURES = [
  { icon: Zap, title: 'سبک و سریع', body: 'کمتر از ۲ مگابایت و باز شدن در کمتر از یک ثانیه' },
  { icon: ShieldCheck, title: 'همه امکانات سایت', body: 'معامله، کیف پول، احراز هویت، کارت بانکی و درگاه پذیرندگی — بدون کم‌و‌کاست' },
  { icon: Lock, title: 'بدون دسترسی اضافه', body: 'تنها دو مجوز می‌گیرد: اینترنت و وضعیت شبکه. به دوربین، مخاطبین و فایل‌های شما کاری ندارد' },
  { icon: LifeBuoy, title: 'پشتیبانی داخل اپ', body: 'بدون خروج از برنامه با کارشناسان گفتگو کنید و پیگیری را همان‌جا ببینید' },
]

const STEPS = [
  'فایل را دانلود کنید.',
  'اگر اندروید پرسید «نصب از این منبع مجاز است؟»، اجازه دهید — این پیام برای همه اپ‌هایی که خارج از فروشگاه نصب می‌شوند نمایش داده می‌شود.',
  'روی فایل دانلودشده بزنید و «نصب» را انتخاب کنید.',
  'با همان حساب سایت وارد شوید؛ نیازی به ثبت‌نام دوباره نیست.',
]

const IOS_STEPS = [
  'همین صفحه را در مرورگر Safari باز کنید.',
  'دکمه‌ی اشتراک‌گذاری را در نوار پایین بزنید — مربعی با فلش رو به بالا.',
  'کمی پایین بروید و «Add to Home Screen» (افزودن به صفحه خانه) را انتخاب کنید.',
  'آیکن ارزینو کنار بقیه‌ی اپ‌ها می‌نشیند. با همان حساب سایت وارد شوید.',
]

/**
 * تشخیص آیفون و اینکه آیا از قبل نصب شده است.
 *
 * `maxTouchPoints` برای آیپدهای جدید لازم است: از iPadOS 13 به بعد سافاری
 * خودش را «Macintosh» معرفی می‌کند و بدون این بررسی، کاربر آیپد راهنمای
 * اندروید را می‌بیند.
 *
 * برای «نصب‌شده» هم دو راه بررسی می‌شود: `navigator.standalone` که مخصوص
 * iOS است و `display-mode: standalone` که استاندارد بقیه است.
 */
function iosState(): { isIos: boolean; installed: boolean; inSafari: boolean } {
  if (typeof navigator === 'undefined') {
    return { isIos: false, installed: false, inSafari: true }
  }
  const ua = navigator.userAgent || ''
  const touch = (navigator as any).maxTouchPoints || 0
  const isIos = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && touch > 1)
  const installed =
    (navigator as any).standalone === true ||
    (typeof window !== 'undefined' &&
      !!window.matchMedia?.('(display-mode: standalone)')?.matches)

  /*
   * «Add to Home Screen» فقط در خودِ سافاری هست.
   *
   * اگر کاربر لینک را از داخل اینستاگرام، تلگرام یا واتساپ باز کرده باشد،
   * صفحه در یک مرورگرِ درون‌برنامه‌ای بالا می‌آید که این گزینه را **ندارد**.
   * کاربر راهنما را دنبال می‌کند، دکمه‌ی اشتراک‌گذاری را می‌زند، گزینه را
   * پیدا نمی‌کند و نتیجه می‌گیرد که راهنما غلط است.
   *
   * تشخیصش: مرورگرهای درون‌برنامه‌ای روی iOS معمولاً `Safari` را در
   * شناسه‌ی خود ندارند، و مرورگرهای دیگر (کروم، فایرفاکس، اج) نشانه‌ی
   * خودشان را دارند.
   */
  const otherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
  const inAppBrowser = isIos && !/Safari/.test(ua)
  return { isIos, installed, inSafari: !otherBrowser && !inAppBrowser }
}

/**
 * دکمه‌ی نصبِ یک‌ضربه‌ای — هرجا که مرورگر اجازه بدهد.
 *
 * ── چرا این دکمه روی آیفون نیست ────────────────────────────────────────
 * کروم و اج رویدادی به نام `beforeinstallprompt` می‌فرستند که می‌شود
 * نگه‌اش داشت و بعد با یک کلیک کاربر، پنجره‌ی نصبِ خودِ سیستم را باز کرد.
 * سافاری این رویداد را **پیاده نکرده** و اپل هیچ API دیگری هم برای
 * اجرای «Add to Home Screen» از داخل صفحه نمی‌دهد. تصمیم عمدیِ اپل است،
 * نه چیزی که بشود دورش زد.
 *
 * پس این قلاب روی آیفون هرگز چیزی نمی‌گیرد و دکمه ساخته نمی‌شود؛ آنجا
 * راهنمای دستی تنها راه است.
 */
function useInstallPrompt() {
  const [prompt, setPrompt] = useState<any>(null)

  useEffect(() => {
    const onAvailable = (e: any) => {
      // بدون این، کروم پنجره‌ی خودش را سرِ خود نشان می‌دهد و دیگر دستِ ما نیست
      e.preventDefault()
      setPrompt(e)
    }
    const onInstalled = () => setPrompt(null)
    window.addEventListener('beforeinstallprompt', onAvailable)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onAvailable)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const install = async () => {
    if (!prompt) return
    try {
      prompt.prompt()
      await prompt.userChoice
    } finally {
      // رویداد یک‌بارمصرف است؛ نگه‌داشتنش یعنی دکمه‌ای که بار دوم کار نمی‌کند
      setPrompt(null)
    }
  }

  return { canInstall: !!prompt, install }
}

/**
 * بخش آیفون.
 *
 * ── چرا اپ آیفون نداریم و این‌طور گفته شده ─────────────────────────────
 * ساخت اپ iOS به مک و حساب توسعه‌دهنده‌ی اپل نیاز دارد و اپ استور هم در
 * ایران سرویس نمی‌دهد. به‌جای سکوت درباره‌ی این موضوع — که کاربر آیفون را
 * سردرگم می‌کند — صریح گفته شده، بلافاصله با راهی که همین حالا کار می‌کند.
 *
 * نسخه‌ی وب پس از افزودن به صفحه‌ی خانه تمام‌صفحه و با آیکن خودش باز
 * می‌شود؛ برای کاربر تفاوتش با یک اپ معمولی کم است.
 */
function IphoneCard({
  installed, inSafari, primary = false,
}: { installed: boolean; inSafari: boolean; primary?: boolean }) {
  if (installed) {
    return (
      <Card className="mb-6 border-up/25 bg-up/[.05]">
        <div className="flex items-center gap-3 text-sm">
          <Check className="h-5 w-5 shrink-0 text-up" />
          <span className="text-slate-200">
            ارزینو روی صفحه‌ی خانه‌ی شما نصب است و همین حالا از همان اجرا می‌شود.
          </span>
        </div>
      </Card>
    )
  }

  return (
    <Card className={primary ? 'mb-6 border-brand-500/30 bg-brand-500/[.04]' : 'mb-6 mt-4'}>
      <div className="mb-4 flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-brand-500/25 bg-brand-500/10 text-brand-300">
          <Share2 className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-bold text-white">
            آیفون دارید؟ نسخه‌ی نصبی روی صفحه‌ی خانه
          </h2>
          <p className="mt-1.5 text-xs leading-7 text-slate-400">
            ارزینو در اپ استور نیست، ولی لازم هم نیست: نسخه‌ی وب را می‌توانید
            روی صفحه‌ی خانه نصب کنید تا با آیکن خودش، تمام‌صفحه و بدون نوار
            مرورگر باز شود — مثل هر اپ دیگری. همه‌ی امکانات هم دقیقاً همان است.
          </p>
        </div>
      </div>

      {/*
        اگر صفحه در سافاری نیست، راهنمای زیر بی‌فایده است: گزینه‌ی
        «Add to Home Screen» اصلاً در آن منو وجود ندارد. به‌جای اینکه
        کاربر دنبال گزینه‌ای بگردد که نیست، همین‌جا گفته می‌شود و لینک هم
        برای چسباندن در سافاری آماده است.
      */}
      {!inSafari && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-gold/25 bg-gold/[.06] p-3">
          <Info className="h-4 w-4 shrink-0 text-gold" />
          <p className="min-w-[12rem] flex-1 text-xs leading-6 text-slate-300">
            این صفحه در سافاری باز نشده و گزینه‌ی «Add to Home Screen» فقط
            آنجاست. لینک را کپی کنید و در سافاری بازش کنید.
          </p>
          <CopyButton text={SITE_URL + '/app'} label="کپی لینک" />
        </div>
      )}

      <ol className="space-y-3 border-t border-white/[.06] pt-4">
        {IOS_STEPS.map((s, i) => (
          <li key={i} className="flex gap-3 text-xs leading-7 text-slate-400">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg border border-brand-500/30 bg-brand-500/10 text-[11px] font-bold text-brand-200">
              {fa(i + 1)}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>

      <p className="mt-4 border-t border-white/[.06] pt-4 text-[11px] leading-6 text-slate-500">
        نصبی نیاز به دانلود فایل ندارد و فضایی هم اشغال نمی‌کند. برای حذف،
        مثل هر اپ دیگری آیکنش را نگه دارید و پاک کنید.
      </p>
    </Card>
  )
}

export default function AppDownload() {
  useSeo({
    title: 'دانلود اپلیکیشن ارزینو — اندروید و آیفون',
    description: 'اپلیکیشن ارزینو؛ خرید و فروش رمزارز، کیف پول و احراز هویت. برای اندروید از کافه‌بازار یا دانلود مستقیم، و برای آیفون نصب روی صفحه خانه.',
  }, [])

  const [info, setInfo] = useState<AppInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [ios] = useState(iosState)
  const { canInstall, install } = useInstallPrompt()
  const win = info?.windows

  useEffect(() => {
    get('/app/version')
      .then((r: any) => setInfo(r))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false))
  }, [])

  const ready = !!info?.enabled && (!!info.url || !!info.bazaarUrl)

  return (
    <Shell>
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 text-center">
          <span className="mx-auto mb-5 grid h-20 w-20 place-items-center rounded-3xl bg-gradient-to-br from-brand-500 to-aqua shadow-glow">
            <Smartphone className="h-10 w-10 text-white" />
          </span>
          <h1 className="text-2xl font-black text-white sm:text-3xl">اپلیکیشن ارزینو</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-slate-400">
            همه امکانات سایت، در جیب شما — روی اندروید و آیفون.
          </p>
        </div>

        {/*
          روی آیفون، بخش آیفون **بالای** بخش اندروید می‌آید.

          کاربری که با آیفون این صفحه را باز کرده، فایل APK به دردش
          نمی‌خورد؛ اگر اول آن را ببیند فکر می‌کند ارزینو برای گوشی او
          چیزی ندارد و صفحه را می‌بندد. روی بقیه‌ی دستگاه‌ها ترتیب برعکس
          است و بخش آیفون پایین‌تر می‌نشیند.
        */}
        {ios.isIos && <IphoneCard installed={ios.installed} inSafari={ios.inSafari} primary />}

        {isNativeApp() && (
          <Card className="mb-6 border-up/25 bg-up/[.05]">
            <div className="flex items-center gap-3 text-sm">
              <Check className="h-5 w-5 shrink-0 text-up" />
              <span className="text-slate-200">شما هم‌اکنون از داخل اپلیکیشن ارزینو این صفحه را می‌بینید.</span>
            </div>
          </Card>
        )}

        {loading ? <Rows n={3} h="h-20" /> : !ready ? (
          <Card>
            <Empty
              icon={<Info className="h-6 w-6" />}
              text="اپلیکیشن اندروید هنوز منتشر نشده است؛ به‌زودی از همین صفحه قابل دانلود خواهد بود."
            />
          </Card>
        ) : (
          <>
            <Card className="mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-bold text-white">نسخه {fa(info!.versionName)}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                    {info!.sizeMb > 0 && <span>حجم {num(info!.sizeMb, 1)} مگابایت</span>}
                    <span>اندروید {fa(info!.minAndroid)} به بالا</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {info!.bazaarUrl && (
                    <a href={info!.bazaarUrl} target="_blank" rel="noreferrer" className="btn-ghost !py-3">
                      دریافت از کافه‌بازار
                    </a>
                  )}
                  {info!.url && (
                    <a href={info!.url} className="btn-primary !py-3">
                      <Download className="h-4 w-4" /> دانلود مستقیم
                    </a>
                  )}
                </div>
              </div>
              {info!.notes && (
                <p className="mt-4 border-t border-white/[.06] pt-4 text-xs leading-6 text-slate-400">
                  <b className="text-slate-300">تغییرات این نسخه: </b>{info!.notes}
                </p>
              )}
            </Card>

            <Card className="mb-6">
              <h2 className="mb-4 font-bold text-white">نصب در چهار قدم</h2>
              <ol className="space-y-3">
                {STEPS.map((s, i) => (
                  <li key={i} className="flex gap-3 text-xs leading-7 text-slate-400">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg border border-brand-500/30 bg-brand-500/10 text-[11px] font-bold text-brand-200">
                      {fa(i + 1)}
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </Card>
          </>
        )}

        {/*
          دکمه‌ی نصبِ یک‌ضربه‌ای.

          فقط وقتی ساخته می‌شود که مرورگر واقعاً `beforeinstallprompt` را
          فرستاده باشد — یعنی کروم یا اج روی اندروید و دسکتاپ. دکمه‌ای که
          همیشه دیده شود ولی نصفِ موارد کاری نکند، بدتر از نبودنش است.
        */}
        {canInstall && (
          <Card className="mb-6 border-brand-500/30 bg-brand-500/[.04]">
            <div className="flex flex-wrap items-center gap-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-brand-500/25 bg-brand-500/10 text-brand-300">
                <Plus className="h-5 w-5" />
              </span>
              <div className="min-w-[12rem] flex-1">
                <div className="text-sm font-bold text-white">نصب نسخه‌ی وب روی همین دستگاه</div>
                <p className="mt-1 text-xs leading-6 text-slate-400">
                  بدون دانلود فایل؛ ارزینو با آیکن خودش روی صفحه‌ی خانه می‌نشیند و
                  تمام‌صفحه باز می‌شود.
                </p>
              </div>
              <Button onClick={install}>
                <Plus className="h-4 w-4" /> نصب
              </Button>
            </div>
          </Card>
        )}

        {/*
          نسخه ویندوز.

          برخلاف بخش آیفون این یکی فایل واقعی دارد، پس فقط وقتی نشان داده
          می‌شود که هم در پنل روشن شده باشد و هم نشانی‌اش پر باشد — وگرنه
          کاربر دکمه‌ای می‌بیند که به هیچ‌جا نمی‌رود.
        */}
        {win?.enabled && win.url && (
          <Card className="mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-brand-500/25 bg-brand-500/10 text-brand-300">
                <Monitor className="h-5 w-5" />
              </span>
              <div className="min-w-[13rem] flex-1">
                <div className="text-sm font-bold text-white">
                  نسخه ویندوز {fa(win.version)}
                </div>
                <p className="mt-1 text-xs leading-6 text-slate-400">
                  بدون نیاز به دسترسی مدیر
                  {win.sizeMb > 0 && <> — {num(win.sizeMb, 1)} مگابایت</>}. پوشه را
                  از حالت فشرده خارج کنید و <b className="text-slate-300">arzino.exe</b> را
                  اجرا کنید.
                </p>
              </div>
              <a href={win.url} className="btn-primary !py-3">
                <Download className="h-4 w-4" /> دانلود برای ویندوز
              </a>
            </div>
            <p className="mt-4 border-t border-white/[.06] pt-4 text-[11px] leading-6 text-slate-500">
              ویندوز ۱۰ و ۱۱. هر دو فایل داخل پوشه باید کنار هم بمانند؛
              <span className="num" dir="ltr"> WebView2Loader.dll </span>
              موتور نمایش صفحه است و بدون آن برنامه بالا نمی‌آید.
              <br />
              اگر هنگام اجرا پیام «Windows protected your PC» را دیدید، روی
              «More info» و سپس «Run anyway» بزنید — این پیام برای هر برنامه‌ای که
              گواهی امضای تجاری نداشته باشد نمایش داده می‌شود.
            </p>
          </Card>
        )}

        {!ios.isIos && <IphoneCard installed={ios.installed} inSafari={ios.inSafari} />}

        <div className="grid gap-4 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <Card key={f.title} className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-brand-500/25 bg-brand-500/10 text-brand-300">
                <f.icon className="h-5 w-5" />
              </span>
              <div>
                <div className="text-sm font-bold text-white">{f.title}</div>
                <p className="mt-1 text-xs leading-6 text-slate-400">{f.body}</p>
              </div>
            </Card>
          ))}
        </div>

        <p className="mt-6 text-center text-[11px] leading-6 text-slate-500">
          اپلیکیشن ارزینو فقط از همین صفحه و کافه‌بازار منتشر می‌شود. فایلی که از
          جای دیگری گرفته باشید ممکن است دستکاری‌شده باشد — نصبش نکنید.
        </p>
      </div>
    </Shell>
  )
}
